import { v4 as uuid } from 'uuid';

import { isCommonNodeRoot } from '@utils/tree/commonTreeHelper';
import { createFlatTreePath } from '@utils/treeFormatter/treeFormatter';

import { ETreeTypeNode } from '@enums/tree';

import { ECompactXMLDataType } from './constants';

import {
	addClosingTagsXML,
	addLastMapTreeChild,
	toggleEmptyObjectNode,
} from './helpers';
import { IFlatXMLTreeNode, INonCompactXMLData, ITagStack, TMapTree } from './interface';

export const updateXMLTreeTags = (flatData: TMapTree) => {
	const keys = [];
	const stack: ITagStack[] = [];

	for (const key of flatData.keys()) {
		keys.push(key);
	}

	addClosingTagsXML(flatData, stack, 0, keys);
	addLastMapTreeChild(flatData, keys);

	return flatData;
};

export const insertMapItemAfterIndex = (
	map: TMapTree,
	parentId: string,
	newKey: string,
	newValue: IFlatXMLTreeNode,
): TMapTree => {
	const entries = Array.from(map.entries());
	const parentIndex = entries.findIndex(([key]) => key === parentId);
	const fromIndex = parentIndex + 1;

	const parentEntry = entries[parentIndex];

	const parentTags = parentEntry[1].tags || [];

	if (parentEntry[1].isEmptyObject) {
		parentEntry[1] = {
			...parentEntry[1],
			tags: [],
			isEmptyObject: false,
		};
	}

	entries.splice(fromIndex, 0, [newKey, { ...newValue, tags: parentTags }]);

	const mapWithUpdatedLineNumbers = new Map(entries);

	return mapWithUpdatedLineNumbers;
};

export const createNestedObjectFromFlatArray = (
	flatData: TMapTree,
): {
	elements: INonCompactXMLData[];
} => {
	const result: INonCompactXMLData[] = [];
	const nodesByParentPath = new Map<string, IFlatXMLTreeNode[]>();

	flatData.forEach((node) => {
		const nodes = nodesByParentPath.get(node.parentPath) || [];
		nodes.push(node);
		nodesByParentPath.set(node.parentPath, nodes);
	});

	const rootNodes = nodesByParentPath.get('') || [];

	const convertToNestedStructure = (node: IFlatXMLTreeNode): INonCompactXMLData => {
		const elements: INonCompactXMLData[] = [];

		if (node.nodeType === ETreeTypeNode.object) {

			const childNodes = nodesByParentPath.get(node.path) || [];
			elements.push(...childNodes.map(convertToNestedStructure));
		} else if (node.value !== undefined) {
			elements.push({
				type: ECompactXMLDataType.text,
				text: node.value,
				name: '',
				elements: [],
			});
		}

		const result: INonCompactXMLData = {
			type: ECompactXMLDataType.element,
			name: node.key,
			elements: elements.length > 0 ? elements : [],
		};

		if (node.description) {
			result.attributes = {
				xmlns: node.description,
			};
		}

		return result;
	};

	result.push(...rootNodes.map(convertToNestedStructure));

	return { elements: result };
};

export const createFlatArrayFromXmlObjectIterative = ({
	data,
	flatData = new Map(),
	nonEditableKeys,
	skippedParameters = [],
}: {
	data: INonCompactXMLData[];
	flatData?: TMapTree;
	nonEditableKeys?: Set<string>;
	skippedParameters?: string[];
}): TMapTree => {
	const stack: Array<{
		node: INonCompactXMLData;
		depth: number;
		parentPath: string;
		keysParentPath: string;
	}> = data.map((node) => ({
		node,
		depth: 0,
		parentPath: '',
		keysParentPath: '',
	}));

	while (stack.length > 0) {
		const current = stack.shift();

		if (!current || !current.node) {
			continue;
		}

		const id = uuid();

		const keysPath = createFlatTreePath(current.node.name, current.keysParentPath);
		const isElementType = current.node?.elements?.[0]?.type === ECompactXMLDataType.element;
		const nodeType = current.node.nodeType;

		flatData.set(
			id,
			{
				key: current.node.name,
				path: createFlatTreePath(id, current.parentPath),
				depth: current.depth,
				keysPath: keysPath,
				nodeType: nodeType === ETreeTypeNode.object || nodeType === ETreeTypeNode.arrayObjects || isElementType
					? isElementType
						? ETreeTypeNode.object
						: nodeType
					: undefined,
				defaultValue:
					current.node?.elements?.length === 0
						? ''
						: current.node?.elements?.[0]?.text,
				parentPath: current.parentPath,
				lineNumber: 0,
				description: current.node?.attributes?.xmlns,
				isCollapsed: false,
				isHiddenChildren: false,
				lastChild: null,
				isEmptyObject: false,
				tags: [],
				value:
					current.node?.elements?.length === 0
						? ''
						: current.node?.elements?.[0]?.text,
				isNonEditable:
					!isElementType
						? nonEditableKeys
							? nonEditableKeys.has(current.node.name)
							: false
						: true,
				isChecked: skippedParameters.includes(keysPath),
				id,
				isObjectAdded: false,
			},
		);

		if (current?.node?.elements?.[0]?.type === ECompactXMLDataType.element) {
			for (let i = current.node.elements.length - 1; i >= 0; i--) {
				const childNode = current.node.elements[i];
				stack.unshift({
					node: childNode,
					depth: current.depth + 1,
					parentPath: createFlatTreePath(
						id,
						current.parentPath,
					),
					keysParentPath: createFlatTreePath(
						current.node.name,
						current.keysParentPath,
					),
				});
			}
		}
	}

	return flatData;
};

export const getXMLTags = (parentKey?: string) => {
	return {
		openedTag: parentKey ? `<${parentKey}>` : '',
		closedTag: parentKey ? `</${parentKey}>` : '',
	};
};

export const getLastChild = (
	map: TMapTree,
	parentPath: string,
	startIndex: number,
	keys: string[],
) => {
	let lastChild: IFlatXMLTreeNode | null = null;

	for (let i = startIndex; i < keys.length; i++) {
		const node = map.get(keys[i])!;

		if (!node) {
			continue;
		}

		if (isCommonNodeRoot(node, parentPath)) {
			lastChild = node;
		} else {
			break;
		}
	}

	return lastChild;
};

export const toggleFlatXMLTree = (
	map: TMapTree,
	id: string,
	nodePath: string,
	keys: string[],
	isExpandNode?: boolean,
) => {
	let lastNodeSameDepth: IFlatXMLTreeNode | null = null;
	let lastNode: IFlatXMLTreeNode | null = null;

	const parentNode = map.get(id)!;
	const parentIndex = keys.indexOf(id);
	const { lastChild } = parentNode;

	const isParentCollapsing = isExpandNode ? false : !parentNode.isHiddenChildren;
	const parentNodeTagIndex = lastChild ? lastChild.tags.findIndex((tag) => tag.id === parentNode.id) : -1;

	if (parentNode.isEmptyObject) {
		toggleEmptyObjectNode(parentNode, isParentCollapsing, lastChild);

		return;
	}

	if (parentNode.tags.length && !isParentCollapsing) {
		parentNode.tags = [];
	}

	parentNode.isHiddenChildren = isParentCollapsing;

	for (let i = parentIndex + 1; i < keys.length; i++) {
		const node = map.get(keys[i])!;

		const isCommonRoot = isCommonNodeRoot(node, nodePath);
		const shouldCollapse = isParentCollapsing && node.path !== nodePath && node.depth > parentNode.depth;
		const shouldExpand = !isParentCollapsing && node.depth === parentNode.depth + 1;

		if (!isCommonRoot) {
			break;
		}

		if (shouldCollapse && !node.isCollapsed) {
			node.isCollapsed = true;
			node.isHiddenChildren = true;

			if (node.depth === parentNode.depth + 1) {
				lastNodeSameDepth = node;
			}
		} else if (shouldExpand) {
			node.isCollapsed = false;
			lastNode = node;
		}
	}

	if (!lastChild) {
		return;
	}

	if (isParentCollapsing) {
		parentNode.tags = lastChild.tags.slice(parentNodeTagIndex + 1).filter((tag) => tag.depth < parentNode.depth);

		if (lastNodeSameDepth && lastNodeSameDepth?.id !== lastChild.id) {
			lastNodeSameDepth.tags = lastChild.tags.slice(parentNodeTagIndex + 1);
		}
	} else if (lastNode) {
		lastNode.tags = lastChild.tags.slice(parentNodeTagIndex);
	}
};

export const convertXMLToFlatTreeData = (
	data: INonCompactXMLData[],
	nonEditableKeys: Set<string> =  new Set(),
	skippedParameters: string[] = [],
) => {
	const flatData = createFlatArrayFromXmlObjectIterative({
		data,
		nonEditableKeys,
		skippedParameters,
	});

	return updateXMLTreeTags(flatData);
};

export const getHighlightedText = (
	text: string,
	className: string,
	searchValue?: string,
) => {
	if (!searchValue) {
		return text;
	}

	const parts = text.split(new RegExp(`(${searchValue})`, 'gi'));

	return parts.map((part, index) => {
		return part.toLowerCase() === searchValue.toLowerCase()
			? (
				<span
					key={index}
					className={className}
				>
					{part}
				</span>
			) : part;
	});
};

